import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutChangeOptions } from "./autogen/AutogenLayoutChangeOptions";
import { TableRow, TableRowCreationEvent, Textbox, TableRowDisplayEvent, TableRowExpansionEvent, Table, Panel, TableRowMode, CrudDecoratorCloseEvent, DataSourceExecutionEvent, DataSourceAction, TableRowBeforeSaveEvent, DataSourceValidationEvent, DropdownSelectionEvent, Checkbox, ClickEvent, DataSource } from "@mcleod/components";
import { RowEdiChangeField } from "./models/ModelEdiChangeField";
import { EdiChangeField } from "./EdiChangeField";
import { ReflectiveDialogs } from "@mcleod/components/src/base/ReflectiveDialogs";
import { CommonDialogs } from "@mcleod/common/src/CommonDialogs";

export class ChangeOptions extends AutogenLayoutChangeOptions {
    private _profileID: string;
    public parentNeedsSaving: (needsSaving: boolean) => void;
    private _parentDataSource: DataSource;

    private _fieldsNeedSaving: boolean = false; // Track if any fields need saving
    private _revertRows: ModelRow[] = [];
    private _expandedRowsWithChanges: Set<number> = new Set(); // Track which expanded rows have unsaved changes
    public get parentDataSource(): DataSource {
        return this._parentDataSource;
    }
    public set parentDataSource(value: DataSource) {
        this._parentDataSource = value;
    }
    public get fieldsNeedSaving(): boolean {
        return this._fieldsNeedSaving;
    }
    public set fieldsNeedSaving(value: boolean) {
        this._fieldsNeedSaving = value;
    }

    private _generatedFieldRecords: Map<string, any[]> = new Map(); // Store field records by table name

    public get profileID(): string {
        return this._profileID;
    }

    public set profileID(value: string) {
        this._profileID = value;
        if (!StringUtil.isEmptyString(value)) {
            this.sourceEdiChangeField.search({ profile_id: this.profileID, tables_only: true });
        }
    }
    override async onLoad() {
        // Add a listener to save all expanded layout changes when the main data source is being saved
        if (this.sourceEdiChangeField) {
            this.sourceEdiChangeField.addBeforeExecutionListener(async (event: DataSourceExecutionEvent) => {
                // Check if this is a save operation (ADD or UPDATE)
                if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE || this.fieldsNeedSaving) {
                    console.log('sourceEdiChangeField beforeExecution triggered for action:', event.getAction());
                    // Save all changes from expanded layouts
                    await this.saveAllExpandedLayoutChangeFields();
                }
            });
        }

        if (this.sourceEdiorderProfileDisplay) {
            this.sourceEdiorderProfileDisplay.addBeforeExecutionListener(async (event: DataSourceExecutionEvent) => {
                // Check if this is a save operation (ADD or UPDATE)
                if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
                    console.log('sourceEdiorderProfileDisplay beforeExecution triggered for action:', event.getAction());
                    // Save all changes from expanded layouts
                    await this.saveAllExpandedLayoutChangeFields();
                }
            });
        }
    }
    /** This is an event handler for the onRowCreate event of tableChangeOptions. */
    tableChangeOptionsOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.getTableRow();
        if (tableRow.isQuickAddRow()) {
            tableRow.data.set("direction", "O");
            tableRow.data.set("profile_id", this.profileID);
        }
        const originalDeleteRow = tableRow.deleteRow.bind(tableRow);
        tableRow.deleteRow = (afterDelete?: () => {}) => {
            ReflectiveDialogs.showYesNo("Are you sure you want to delete this record?", "Confirm Deletion").then(async (clickedYes) => {
                if (clickedYes) {
                    try {
                        await Api.post("lme/datafusion/delete-edi-change-field", { profile_id: this.profileID, table_name: tableRow.data.get("table_name") }).then(() => {
                            this.tableChangeFieldTableOnly.removeRow(tableRow.index);
                        });

                    } catch (error) {
                        CommonDialogs.showError("Error deleting record: " + error);
                        // Handle error as needed
                    }
                }
            });
        };
    }

    /** This is an event handler for the onRowDisplay event of tableChangeOptions. */
    tableChangeOptionsOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.getTableRow();
        if (row.isQuickAddRow()) {
            row.expandable = false;
        } else {
            const textboxTableName = row.findComponentById("textboxTableName") as Textbox;
            textboxTableName.enabled = false;
            if (row.data.get("table_name") === "movement") {
                this.disableTableCheckboxes(row, false);
            }

        }
        const textTableName = row.findComponentById("textboxTableName") as Textbox;
        if (textTableName) {
            textTableName.lookupModelDisabled = true;
        }
    }

    /** This is an event handler for the onRowExpand event of tableChangeFieldTableOnly. */
    async tableChangeFieldTableOnlyOnRowExpand(event: TableRowExpansionEvent) {
        const tableRow: TableRow = event.target as TableRow;

        // Only handle expansion events - collapse is handled via property interception
        if (!event.isExpanding) {
            return;
        }

        // Existing expand logic continues here
        const expandComponent = await tableRow.getExpansionComponentLoaded() as Panel;

        const tableName = tableRow.data.get("table_name");
        const ediChangeField = expandComponent.findComponentById("layoutChangeField") as EdiChangeField;
        ediChangeField.sourceEdiChangeField.data.filter((row: RowEdiChangeField) => {
            row.get("table_name") === tableName && row.get("profile_id") === this.profileID && !StringUtil.isEmptyString(row.get("field_name"));
        }); // Clear existing data

        ediChangeField.tableName = tableName;
        ediChangeField.profileID = this.profileID;

        // Set up the parentNeedsSaving callback
        ediChangeField.parentNeedsSaving = (needsSaving: boolean) => {
            this.sourceEdiChangeField.notifyHasChangedComponents(needsSaving);
            this.sourceEdiorderProfileDisplay.notifyHasChangedComponents(needsSaving);

            // Track changes for this specific row
            if (needsSaving) {
                this._expandedRowsWithChanges.add(tableRow.index);
            } else {
                this._expandedRowsWithChanges.delete(tableRow.index);
            }
        };

        // Set up collapse warning mechanism by overriding the expanded property
        this.setupCollapseInterception(tableRow);

        // Check if there are pending field records to populate (from Generate Defaults)
        const pendingFieldRecords = (tableRow as any)._pendingFieldRecords;
        const generatedFieldRecords = this._generatedFieldRecords.get(tableName);

        if (pendingFieldRecords && pendingFieldRecords.length > 0) {
            // Populate with pending field records
            for (const fieldRecord of pendingFieldRecords) {
                // Convert ModelRow to plain object if necessary
                const recordData = fieldRecord.data ? fieldRecord.data : fieldRecord;

                const fieldModelRow = new ModelRow(ediChangeField.sourceEdiChangeField.url, false, recordData);
                fieldModelRow._appending = true; // Mark as appending to bypass certain validations
                const fieldAddResult = ediChangeField.tableChangeField.addRow(fieldModelRow, { mode: TableRowMode.ADD }, { display: true, addToData: true });
                ediChangeField.sourceEdiChangeField.data.push(fieldModelRow); // Ensure it's in the data array
                if (fieldAddResult.row) {
                    fieldAddResult.row.displayData(fieldModelRow, ediChangeField.sourceEdiChangeField.data, fieldAddResult.row.index);
                } else {
                    console.error('Failed to add field row for record:', fieldRecord);
                }
            }
            // Clear the pending records as they've been processed
            delete (tableRow as any)._pendingFieldRecords;
        } else if (generatedFieldRecords && generatedFieldRecords.length > 0) {
            // Populate with generated field records from global storage
            for (const fieldRecord of generatedFieldRecords) {
                // Convert ModelRow to plain object if necessary
                const recordData = fieldRecord.data ? fieldRecord.data : fieldRecord;

                const fieldModelRow = new ModelRow(ediChangeField.sourceEdiChangeField.url, false, recordData);
                fieldModelRow._appending = true; // Mark as appending to bypass certain validations
                const fieldAddResult = ediChangeField.tableChangeField.addRow(fieldModelRow, { mode: TableRowMode.ADD }, { display: true, addToData: true });
                ediChangeField.sourceEdiChangeField.data.push(fieldModelRow); // Ensure it's in the data array
                if (fieldAddResult.row) {
                    fieldAddResult.row.displayData(fieldModelRow, ediChangeField.sourceEdiChangeField.data, fieldAddResult.row.index);
                } else {
                    console.error('Failed to add generated field row for record:', fieldRecord);
                }
            }

            // Clear the generated records as they've been processed
            this._generatedFieldRecords.delete(tableName);
        } else {
            // Normal behavior - search for existing records
            ediChangeField.sourceEdiChangeField.search({ profile_id: this.profileID, table_name: tableName, fields_only: true, tables_only: false });
        }
    }

    /**
     * Sets up collapse interception by intercepting the expander button click
     */
    private setupCollapseInterception(tableRow: TableRow): void {
        // Get the expander button and intercept its click event
        const expanderButton = tableRow.expanderButton;
        if (expanderButton) {

            // Access the DOM element to intercept clicks at the DOM level
            const buttonElement = expanderButton._element;
            if (buttonElement) {
                // Add a DOM event listener that will run before the component's click listeners
                const domClickHandler = async (event: Event) => {
                    // If row is currently expanded and has unsaved changes, show warning
                    if (tableRow.expanded && this._expandedRowsWithChanges?.has(tableRow.index)) {
                        // Prevent the default click behavior
                        event.preventDefault();
                        event.stopPropagation();

                        const userWantsToSave = await CommonDialogs.showYesNo(
                            "You have unsaved changes in this expanded section. Do you want to save them before collapsing?",
                            "Unsaved Changes Warning"
                        );

                        if (userWantsToSave) {
                            // User wants to save - post the main datasource first
                            try {
                                if (this.parentDataSource) {
                                    await this.saveAllExpandedLayoutChangeFields();
                                    await this.parentDataSource.post();
                                }
                                // Clean up tracking for this row since changes have been saved
                                this._expandedRowsWithChanges?.delete(tableRow.index);

                                // Manually trigger the collapse
                                tableRow.expanded = false;
                            } catch (error) {
                                CommonDialogs.showError("Error saving changes: " + error);
                                return; // Don't collapse if save failed
                            }
                        } else if (userWantsToSave === false) {
                            // User wants to discard changes and continue with collapse
                            // Clean up tracking for this row since user confirmed losing changes
                            this._expandedRowsWithChanges?.delete(tableRow.index);

                            // Manually trigger the collapse
                            tableRow.expanded = false;
                        }
                        // If userWantsToSave === undefined (user cancelled/closed dialog), do nothing (keep expanded)
                    }
                    // If no unsaved changes, let the normal click proceed
                };

                // Store reference to our DOM handler so we can remove it later if needed
                if (!expanderButton['_customDomClickHandler']) {
                    expanderButton['_customDomClickHandler'] = domClickHandler;
                    buttonElement.addEventListener('click', domClickHandler, true); // Use capture phase
                }
            }
        }
    }    /**
     * Loops through all tableChangeFieldTableOnly table rows, gets their expanded panels,
     * collects all rows from expanded EdiChangeField layouts, and posts them together.
     * Follows the same pattern as TenderProfile.postAllExpandedLayoutDatasources
     * This method is public so it can be called manually for testing or from other components
     */
    public async saveAllExpandedLayoutChangeFields(): Promise<void> {
        try {
            console.log('saveAllExpandedLayoutChangeFields called');

            // Get all table rows from tableChangeFieldTableOnly
            const allRows = this.tableChangeFieldTableOnly.allRows;
            const allDataRowsToPost = [];

            console.log(`Processing ${allRows.length} rows in tableChangeFieldTableOnly`);

            // First pass: collect all rows from expanded panels
            for (const tableRow of allRows) {
                console.log(`Checking row ${tableRow.index}, expanded: ${tableRow.expanded}, table_name: ${tableRow.data.get("table_name")}`);

                // Check if the row is expanded
                if (tableRow.expanded) {
                    console.log(`Processing expanded row with table_name: ${tableRow.data.get("table_name")}`);

                    // Get the expanded panel
                    const expandedPanel = tableRow.getExpansionComponent() as Panel;
                    console.log('expandedPanel found:', !!expandedPanel);

                    if (expandedPanel) {
                        // Find the EdiChangeField layout within the expanded panel
                        const expandedLayout = expandedPanel.findComponentById("layoutChangeField") as EdiChangeField;
                        // expandedLayout.sourceEdiChangeField.post();//TODO Revisit -- maybe can remove everything below if this works
                        console.log('expandedLayout found:', !!expandedLayout);
                        console.log('tableChangeField found:', !!expandedLayout?.tableChangeField);

                        if (expandedLayout && expandedLayout.tableChangeField) {
                            // Collect all data rows from this expanded layout (not just changed ones)
                            const tableRows = expandedLayout.tableChangeField.rows;
                            console.log(`tableChangeField has ${tableRows.length} rows`);

                            const allRows = tableRows
                                .filter(tRow => tRow.data) // Only ensure data exists
                                .map(tRow => tRow.data);

                            console.log(`Found ${allRows.length} data rows in table_name: ${tableRow.data.get("table_name")}`);

                            // Add all rows to our collection array
                            allDataRowsToPost.push(...allRows);
                        } else {
                            console.warn(`No expandedLayout or tableChangeField found for expanded row with table_name: ${tableRow.data.get("table_name")}`);
                        }
                    } else {
                        console.warn(`No expandedPanel found for expanded row with table_name: ${tableRow.data.get("table_name")}`);
                    }
                }
            }

            // Second pass: post all collected rows together
            if (allDataRowsToPost.length > 0) {
                console.log(`Posting ${allDataRowsToPost.length} total data rows from all expanded panels`);

                try {
                    // Create promises for all posts
                    const savePromises = allDataRowsToPost.map(row => {
                        console.log('Posting row with data:', { profile_id: row.get('profile_id'), table_name: row.get('table_name'), field_name: row.get('field_name') });
                        return row.post();
                    });

                    // Wait for all saves to complete
                    await Promise.all(savePromises);

                    console.log(`Successfully posted all ${allDataRowsToPost.length} rows from expanded layouts`);

                    // Clear the tracking of unsaved changes for all rows since they've been saved
                    this._expandedRowsWithChanges.clear();
                } catch (error) {
                    console.error(`Failed to post collected rows from expanded layouts`, error);
                    throw error;
                }
            } else {
                console.log(`No data rows found in expanded panels to post`);
            }

            // Third pass: ensure all expanded panels stay expanded
            for (const tableRow of allRows) {
                if (tableRow.expanded) {
                    const expandedPanel = tableRow.getExpansionComponent() as Panel;
                    if (expandedPanel) {
                        expandedPanel.expand();
                    }
                }
            }

        } catch (error) {
            console.error("Error saving expanded layout change fields:", error);
            throw error;
        }
    }

    /**
     * Disables the Add and Delete checkboxes for the movement table
     * @param tableRow - The table row containing the checkboxes to disable
     */
    private disableTableCheckboxes(tableRow: TableRow, value: boolean): void {
        const checkAdd = tableRow.findComponentById("checkboxAdd") as Checkbox;
        const checkDelete = tableRow.findComponentById("checkboxDelete") as Checkbox;
        checkAdd.checked = value;
        checkAdd.enabled = value;
        checkDelete.checked = value;
        checkDelete.enabled = value;
    }

    /** This is an event handler for the onValidate event of sourceEdiChangeField. */
    sourceEdiChangeFieldOnValidate(event: DataSourceValidationEvent) {
        const data = event.row;
        const tableName = data?.get("table_name");

        if (tableName) {
            // Check if this table_name already exists in the table's rows
            const existingRows = this.tableChangeFieldTableOnly.allRows;
            const duplicateExists = existingRows.some(tableRow =>
                tableRow.data &&
                tableRow.data.get("table_name") === tableName &&
                tableRow.data !== data // Exclude the current row being validated
            );

            if (duplicateExists && !this.fieldsNeedSaving) {
                CommonDialogs.showError(`Change option record already exists for table ${tableName}`);
                // Add validation error to prevent saving
                event.validationResults.push({
                    isValid: false,
                    validationMessage: `Change option record already exists for table ${tableName}`,
                    component: this.tableChangeFieldTableOnly
                });
                return;
            }
        }
    }

    /** This is an event handler for the afterDropdownSelection event of textboxTableName. */
    textboxTableNameAfterDropdownSelection(event: DropdownSelectionEvent) {
        const textbox = event.target as Textbox;
        const tableName = event.selection?.value;
        const currentTableRow = TableRow.getContainingTableRow(textbox); // Get the current table row being edited

        //Vaidate no duplicates
        if (tableName && currentTableRow) {
            // Check if this table_name already exists in the table's rows
            const existingRows = this.tableChangeFieldTableOnly.allRows;
            const duplicateExists = existingRows.some(tableRow =>
                tableRow.data &&
                tableRow.data.get("table_name") === tableName
                // tableRow.index !== currentTableRow.index // Exclude the current row by comparing index
            );

            if (duplicateExists) {
                CommonDialogs.showError(`Change option record already exists for table ${tableName}`);
                // Add validation error to prevent saving
                textbox.selectedItem = null; // Clear the selection
                return;
            }
        }

        if ("movement" === tableName && currentTableRow) {
            this.disableTableCheckboxes(currentTableRow, false);
        } else {
            this.disableTableCheckboxes(currentTableRow, true);
        }
    }

    /** This is an event handler for the onClick event of buttonGenerateDefaults. */
    async buttonGenerateDefaultsOnClick(event: ClickEvent) {

        const userConfirmed = await ReflectiveDialogs.showYesNo(
            "This will remove all current trigger settings for this partner and replace them with the default settings! Continue?",
            "WARNING!"
        );
        if (userConfirmed) {
            //Collect existing rows in case we need to revert
            Api.search("lme/datafusion/edi-change-field", { profile_id: this.profileID, tables_only: false, fields_only: false }).then((searchResult) => {
                // Clear any tracked unsaved changes since we're replacing everything
                this._expandedRowsWithChanges.clear();

                searchResult.data.forEach(rowData => {
                    const modelRow = new ModelRow(this.sourceEdiChangeField.url, false, rowData);
                    this._revertRows.push(modelRow);
                    if (!StringUtil.isEmptyString(modelRow.get("field_name"))) {
                        modelRow.delete();//only delete field-level records, the table-level records will be deleted by the linked model posting
                    }
                });
            }).then(() => {
                Api.post("lme/datafusion/generate-change-option-defaults", { profile_id: this.profileID }).then(async (result) => {

                    // Loop through tableChangeFieldTableOnly and remove each row
                    const allRows = this.tableChangeFieldTableOnly.allRows;
                    for (let i = allRows.length - 1; i >= 0; i--) {
                        // Remove from datasource and table
                        this.tableChangeFieldTableOnly.deleteRow(allRows[i].index);
                    }

                    // Process the result and populate tables
                    if (result && result.data.length > 0) {
                        // Separate table-level records and field-level records
                        const tableRecords = result.data.filter(row => StringUtil.isEmptyString(row.field_name));
                        const fieldRecords = result.data.filter(row => !StringUtil.isEmptyString(row.field_name));

                        // Use tableRecords directly - these contain the table-level update indicators
                        for (const tableRecord of tableRecords) {
                            // Create ModelRow instance for the table record
                            const tableModelRow = new ModelRow(this.sourceEdiChangeField.url, false, tableRecord);
                            tableModelRow._appending = true; // Mark as appending to bypass certain validations
                            const addRowResult = this.tableChangeFieldTableOnly.addRow(tableModelRow, { mode: TableRowMode.ADD }, { display: true, addToData: true });

                            if (addRowResult.row) {
                                // Force the row to display its data properly
                                addRowResult.row.displayData(tableModelRow, this.sourceEdiChangeField.data, addRowResult.row.index);

                                // Get the corresponding field records for this table
                                const tableName = tableRecord.table_name;
                                const relatedFieldRecords = fieldRecords.filter(fieldRecord =>
                                    fieldRecord.table_name === tableName
                                );

                                // Store field records in global storage for this table
                                if (relatedFieldRecords.length > 0) {
                                    this._generatedFieldRecords.set(tableName, relatedFieldRecords);
                                }

                                // Also store them on the row as backup (for the existing logic)
                                (addRowResult.row as any)._pendingFieldRecords = relatedFieldRecords;
                                await addRowResult.row.expand(); // Expand the row to show fields
                                addRowResult.row.expanded = true; // Mark as expanded
                                this.fieldsNeedSaving = true;
                            }
                        }

                    }
                    if(this.parentDataSource){
                        await this.saveAllExpandedLayoutChangeFields();
                        await this.parentDataSource.post();
                        this.parentDataSource.notifyHasChangedComponents(false);
                        this.parentNeedsSaving(false);
                        this.buttonRevert.visible = true;
                        this.buttonGenerateDefaults.enabled = false;
                    }

                }).catch(error => {
                    CommonDialogs.showError("Error generating default change options: " + error);
                });
            });
        }
    }

    /** This is an event handler for the onClick event of buttonRevert. */
    buttonRevertOnClick(event: ClickEvent) {
        if (this._revertRows && this._revertRows.length > 0) {
            ReflectiveDialogs.showYesNo("This will remove all current trigger settings for this partner and replace them with the previous settings! Continue?", "WARNING!").then(async (userConfirmed) => {
                if (userConfirmed) {
                    try {
                        //Collect existing rows in case we need to revert
                        Api.search("lme/datafusion/edi-change-field", { profile_id: this.profileID, tables_only: false, fields_only: false }).then((searchResult) => {
                            searchResult.data.forEach(rowData => {
                                const modelRow = new ModelRow(this.sourceEdiChangeField.url, false, rowData);
                                if (!StringUtil.isEmptyString(modelRow.get("field_name"))) {
                                    modelRow.delete();//only delete field-level records, the table-level records will be deleted by the linked model posting
                                }
                            });
                        });
                    // Loop through tableChangeFieldTableOnly and remove each row
                    const allRows = this.tableChangeFieldTableOnly.allRows;
                    for (let i = allRows.length - 1; i >= 0; i--) {
                        const tableRow = allRows[i] as TableRow;
                        // Remove from datasource and table
                        this.tableChangeFieldTableOnly.deleteRow(allRows[i].index);
                    }
                        // Separate table-level records and field-level records
                        const tableRecords = this._revertRows.filter(row => StringUtil.isEmptyString(row.get("field_name")));
                        const fieldRecords = this._revertRows.filter(row => !StringUtil.isEmptyString(row.get("field_name")));

                        console.log(`Restoring ${tableRecords.length} table records and ${fieldRecords.length} field records`);

                        // Use tableRecords directly - these contain the table-level update indicators
                        for (const tableRecord of tableRecords) {
                            // Create ModelRow instance for the table record
                            tableRecord._appending = true; // Mark as appending to bypass certain validations
                            const addRowResult = this.tableChangeFieldTableOnly.addRow(tableRecord, { mode: TableRowMode.ADD }, { display: true, addToData: true });

                            if (addRowResult.row) {
                                // Force the row to display its data properly
                                addRowResult.row.displayData(tableRecord, this.sourceEdiChangeField.data, addRowResult.row.index);

                                // Get the corresponding field records for this table
                                const tableName = tableRecord.get("table_name");
                                const relatedFieldRecords = fieldRecords.filter(fieldRecord =>
                                    fieldRecord.get("table_name") === tableName
                                );

                                console.log(`Restoring table: ${tableName} with ${relatedFieldRecords.length} related field records`);

                                // Debug the field records structure
                                if (relatedFieldRecords.length > 0) {
                                    console.log('Sample field record structure:', relatedFieldRecords[0]);
                                    console.log('Field record keys:', Object.keys(relatedFieldRecords[0]));
                                    if (relatedFieldRecords[0].data) {
                                        console.log('Field record data:', relatedFieldRecords[0].data);
                                    }
                                }

                                // Store field records in global storage for this table first
                                if (relatedFieldRecords.length > 0) {
                                    this._generatedFieldRecords.set(tableName, relatedFieldRecords);
                                    // Also store them on the row as backup (for the existing logic)
                                    (addRowResult.row as any)._pendingFieldRecords = relatedFieldRecords;

                                    // Add a small delay to ensure data is properly set before expansion
                                    await new Promise(resolve => setTimeout(resolve, 10));

                                    // Expand the row to show fields - this will trigger the expansion handler
                                    await addRowResult.row.expand();
                                    addRowResult.row.expanded = true; // Mark as expanded
                                    this.fieldsNeedSaving = true;
                                } else {
                                    // If no field records, still expand the row but without field data
                                    await addRowResult.row.expand();
                                    addRowResult.row.expanded = true;
                                }
                            }
                        }

                        if(this.parentDataSource){
                            await this.saveAllExpandedLayoutChangeFields();
                            await this.parentDataSource.post();
                            this.parentDataSource.notifyHasChangedComponents(false);
                            this.parentNeedsSaving(false);
                            this.buttonRevert.visible = false;
                            this.buttonGenerateDefaults.enabled = true;
                        }
                        // Clear the revert storage as we've completed the revert
                        this._revertRows = [];
                        // fieldsNeedSaving should remain true if there were field records restored
                        // Don't set it to false here as it may have been set to true during restoration
                    } catch (error) {
                        CommonDialogs.showError("Error reverting to previous change options: " + error);
                    }
                }
            });
        } else {
            // No revert data available
            CommonDialogs.showError("No previous change options available to revert to.");
        }
    }
}
